import React from 'react';
import { Star, CheckCircle2, Check } from 'lucide-react';
import { useTranslations } from '../hooks/useTranslations';
export function Hero() {
  const {
    t
  } = useTranslations();
  return <section id="hero" className="relative pt-24 pb-16 lg:pt-28 lg:pb-20 overflow-hidden bg-white" data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-198">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-199">
          {/* Left Content */}
          <div className="space-y-6 text-center lg:text-left max-w-2xl mx-auto lg:mx-0" data-id="element-200">
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-gray-900 leading-[1.1] tracking-tight" data-id="element-201">
              {t('hero.title')}
            </h1>

            <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0" data-id="element-202">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col items-center lg:items-start gap-3" data-id="element-203">
              {/* Primary CTA */}
              <a href="https://form.jotform.com/260402880146048" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center text-sm font-bold px-6 py-3 rounded-lg text-white bg-[#00E676] shadow-lg shadow-green-200/50 hover:bg-[#00d169] hover:shadow-xl hover:shadow-green-300/50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 ease-out animate-hero-pulse" data-id="element-204">
                {t('hero.cta')}
              </a>

              {/* Secondary CTA */}
              <button onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} className="inline-flex items-center justify-center text-sm font-bold px-6 py-3 rounded-lg text-[#00E676] border-2 border-[#00E676] bg-transparent hover:bg-[#00E676] hover:text-white active:scale-[0.97] transition-all duration-200 cursor-pointer" data-id="element-205">
                {t('hero.ctaSecondary')}
              </button>

              {/* Microcopy Trust Signals */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-gray-600" data-id="element-206">
                {t('hero.microcopy1') && <div className="flex items-center gap-1.5" data-id="element-207">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-208" />
                    <span data-id="element-209">{t('hero.microcopy1')}</span>
                  </div>}
                {t('hero.microcopy2') && <div className="flex items-center gap-1.5" data-id="element-210">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-211" />
                    <span data-id="element-212">{t('hero.microcopy2')}</span>
                  </div>}
                {t('hero.microcopy3') && <div className="flex items-center gap-1.5" data-id="element-213">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-214" />
                    <span data-id="element-215">{t('hero.microcopy3')}</span>
                  </div>}
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="pt-4 flex flex-col items-center lg:items-start gap-4" data-id="element-216">
              {/* Row 1: Stars + Trustpilot */}
              <div className="flex items-center gap-2" data-id="element-217">
                <div className="flex" data-id="element-218">
                  {[1, 2, 3, 4].map(i => <Star key={i} className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-219" />)}
                  <div className="relative w-5 h-5" data-id="element-220">
                    <Star className="w-5 h-5 text-gray-300 fill-current absolute inset-0" data-id="element-221" />
                    <div className="absolute inset-0 overflow-hidden" style={{
                    width: '80%'
                  }} data-id="element-222">
                      <Star className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-223" />
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-224">
                  {t('hero.trustpilot')}
                </span>
              </div>

              {/* Row 2: Avatars + Clients */}
              <div className="flex items-center text-sm font-medium text-gray-600" data-id="element-225">
                <div className="flex -space-x-2 mr-3" data-id="element-226">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-227">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-228" />
                    </div>)}
                </div>
                {t('hero.trustedBy')}
              </div>
            </div>
          </div>

          {/* Right Content - Image & Background Shape */}
          <div className="relative lg:h-[500px] flex items-center justify-center mt-10 lg:mt-0" data-id="element-229">
            {/* Green Geometric Background */}
            <div className="absolute inset-0 z-0 flex justify-center items-center" data-id="element-230">
              <div className="w-[120%] h-[120%] bg-gradient-to-tr from-[#e6fcf0] to-[#b3f5d1] transform rotate-12 skew-x-12 rounded-[100px] opacity-80" data-id="element-231"></div>
            </div>

            <div className="relative w-full max-w-md lg:max-w-none z-10" data-id="element-232">
              <img src="https://cdn.magicpatterns.com/uploads/kGkyYG1wwa3dggLJRfv8Sm/vecteezy_business-woman-in-glasses-using-her-phone-on-transparent_47308872.png" alt="Professional Business Executive" className="w-full h-auto object-contain relative z-10 scale-110" data-id="element-233" />

              {/* Floating Approval Badge 1 */}
              <div className="absolute -top-2 right-0 md:right-8 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-234">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-235" />
                <p className="text-xs font-bold text-gray-900" data-id="element-236">
                  {t('hero.approvalBadge1')}
                </p>
              </div>

              {/* Floating Approval Badge 2 */}
              <div className="absolute top-1/2 -left-4 md:left-0 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-237">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-238" />
                <p className="text-xs font-bold text-gray-900" data-id="element-239">
                  {t('hero.approvalBadge2')}
                </p>
              </div>

              {/* Floating Approval Badge 3 */}
              <div className="absolute bottom-10 right-0 md:right-10 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2 z-20" data-id="element-240">
                <CheckCircle2 className="w-4 h-4 text-[#00E676]" data-id="element-241" />
                <p className="text-xs font-bold text-gray-900" data-id="element-242">
                  {t('hero.approvalBadge3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}